import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Shield, Clock, CheckCircle, AlertTriangle, BarChart3, Target, Crown, Activity, Percent, Timer, Users, DollarSign, Zap } from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, BarChart, Bar, Cell, PieChart, Pie, Legend 
} from 'recharts';

// Format currency for ALE display
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

export default function StrategicDashboard() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [crownJewels, setCrownJewels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aleMetrics, setAleMetrics] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, crownJewelsRes, aleRes] = await Promise.all([
        api.get('/dashboard/strategic'),
        api.get('/crown-jewels').catch(() => ({ data: [] })),
        api.get('/fair/strategic-metrics').catch(() => ({ data: null }))
      ]);
      setData(dashboardRes.data);
      setCrownJewels(crownJewelsRes.data);
      setAleMetrics(aleRes.data);
    } catch (error) {
      toast.error('Failed to fetch strategic dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    const colors = {
      critical: isDarkMode ? 'text-red-400' : 'text-red-600',
      high: isDarkMode ? 'text-orange-400' : 'text-orange-600',
      medium: isDarkMode ? 'text-yellow-400' : 'text-yellow-600',
      low: isDarkMode ? 'text-green-400' : 'text-green-600'
    };
    return colors[level] || (isDarkMode ? 'text-zinc-400' : 'text-slate-500');
  };

  // Chart colors based on theme
  const chartColors = {
    primary: isDarkMode ? '#6366f1' : '#4f46e5',
    secondary: isDarkMode ? '#00BFFF' : '#00A5E0',
    success: isDarkMode ? '#22c55e' : '#16a34a',
    warning: isDarkMode ? '#f59e0b' : '#d97706',
    danger: isDarkMode ? '#ef4444' : '#dc2626',
    grid: isDarkMode ? '#27272a' : '#e2e8f0',
    text: isDarkMode ? '#71717a' : '#64748b',
  };

  // Use real SLA compliance data from API, with defaults if not available
  const slaComplianceData = data?.sla_compliance || [
    { name: 'Critical', target: 95, actual: 0, color: chartColors.danger },
    { name: 'High', target: 90, actual: 0, color: chartColors.warning },
    { name: 'Medium', target: 85, actual: 0, color: '#ca8a04' },
    { name: 'Low', target: 80, actual: 0, color: chartColors.success },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  // Risk trend data (12 months)
  const riskTrendData = data?.security_posture_trend || [
    { month: 'Jan', risk_score: 72, security_score: 28 },
    { month: 'Feb', risk_score: 68, security_score: 32 },
    { month: 'Mar', risk_score: 65, security_score: 35 },
    { month: 'Apr', risk_score: 58, security_score: 42 },
    { month: 'May', risk_score: 52, security_score: 48 },
    { month: 'Jun', risk_score: 45, security_score: 55 },
    { month: 'Jul', risk_score: 42, security_score: 58 },
    { month: 'Aug', risk_score: 38, security_score: 62 },
    { month: 'Sep', risk_score: 35, security_score: 65 },
    { month: 'Oct', risk_score: 32, security_score: 68 },
    { month: 'Nov', risk_score: 28, security_score: 72 },
    { month: 'Dec', risk_score: data?.risk_score || 25, security_score: 100 - (data?.risk_score || 25) },
  ];

  // Security posture history
  const securityPostureHistory = [
    { quarter: 'Q1 2025', score: 62, vulns: 145, assets: 85 },
    { quarter: 'Q2 2025', score: 68, vulns: 120, assets: 92 },
    { quarter: 'Q3 2025', score: 74, vulns: 98, assets: 105 },
    { quarter: 'Q4 2025', score: 100 - (data?.risk_score || 25), vulns: 72, assets: 118 },
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`px-3 py-2 rounded-lg text-xs font-inter ${
          isDarkMode 
            ? 'bg-zinc-900 border border-zinc-800 text-white' 
            : 'bg-white border border-slate-200 text-slate-900 shadow-lg'
        }`}>
          <p className="font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className={`font-inter text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading strategic insights...</div>
      </div>
    );
  }

  return (
    <div data-testid="strategic-dashboard" className="space-y-4 sm:space-y-6">
      <div>
        <h1 className={`font-inter font-bold text-xl sm:text-2xl lg:text-3xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Strategic Dashboard
        </h1>
        <p className={`font-inter mt-1 text-sm sm:text-base ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Executive-level security posture overview for CISOs</p>
      </div>

      {/* Key Metrics - Executive KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className={`w-8 sm:w-12 h-8 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
              <Shield className={`w-4 sm:w-6 h-4 sm:h-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <span className={`text-[10px] sm:text-xs font-mono uppercase ${getRiskColor(data?.risk_level)}`}>
              {data?.risk_level?.toUpperCase() || 'MEDIUM'}
            </span>
          </div>
          <p className={`text-2xl sm:text-4xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.risk_score || 32}</p>
          <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Risk Score</p>
        </div>

        <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className={`w-8 sm:w-12 h-8 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
              <Clock className={`w-4 sm:w-6 h-4 sm:h-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <TrendingDown className="w-3 sm:w-4 h-3 sm:h-4 text-green-500" />
          </div>
          <p className={`text-2xl sm:text-4xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.mttr_days || 4.2}</p>
          <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Avg MTTR (days)</p>
        </div>

        <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className={`w-8 sm:w-12 h-8 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <CheckCircle className={`w-4 sm:w-6 h-4 sm:h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <TrendingUp className="w-3 sm:w-4 h-3 sm:h-4 text-green-500" />
          </div>
          <p className={`text-2xl sm:text-4xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.compliance_score || 85}%</p>
          <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Compliance Score</p>
        </div>

        <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className={`w-8 sm:w-12 h-8 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
              <Target className={`w-4 sm:w-6 h-4 sm:h-6 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
          </div>
          <p className={`text-2xl sm:text-4xl font-inter font-bold text-green-500`}>
            {100 - (data?.risk_score || 32)}%
          </p>
          <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Security Posture</p>
        </div>
      </div>

      {/* FAIR Model - Financial Risk Quantification - CISO PRIORITY */}
      {aleMetrics && (
        <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'glass border border-violet-500/20' : 'bg-white border border-violet-200 shadow-card'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
            <div className="flex items-center gap-3">
              <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                <DollarSign className={`w-5 sm:w-6 h-5 sm:h-6 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              </div>
              <div>
                <h2 className={`font-inter font-semibold text-lg sm:text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Financial Risk Quantification
                </h2>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  FAIR Model - Annual Loss Expectancy Analysis
                </p>
              </div>
            </div>
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium w-fit ${isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
              <Zap className="w-3 h-3 inline mr-1" />
              FAIR Framework
            </span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {/* Current Risk Exposure */}
            <div className={`rounded-lg sm:rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
              <p className={`text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Current Risk Exposure</p>
              <p className={`text-xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.current_exposure?.total_ale_formatted || '$0'}
              </p>
              <p className={`text-[10px] sm:text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Annual loss from {aleMetrics.current_exposure?.open_vulnerabilities || 0} vulns
              </p>
            </div>

            {/* Savings Achieved */}
            <div className={`rounded-lg sm:rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-100'}`}>
              <p className={`text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Savings (90 days)</p>
              <p className={`text-xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.risk_reduction?.savings_formatted || '$0'}
              </p>
              <p className={`text-[10px] sm:text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                From {aleMetrics.risk_reduction?.closed_vulnerabilities || 0} remediations
              </p>
            </div>

            {/* Risk Reduction */}
            <div className={`rounded-lg sm:rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
              <p className={`text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>Risk Reduction</p>
              <p className={`text-xl sm:text-3xl font-bold text-green-500`}>
                {aleMetrics.risk_reduction?.risk_reduction_percentage?.toFixed(1) || 0}%
              </p>
              <p className={`text-[10px] sm:text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Potential loss avoided
              </p>
            </div>

            {/* Potential Loss */}
            <div className={`rounded-lg sm:rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-100'}`}>
              <p className={`text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Potential Total Loss</p>
              <p className={`text-xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.risk_reduction?.potential_ale_formatted || '$0'}
              </p>
              <p className={`text-[10px] sm:text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                If no remediation
              </p>
            </div>
          </div>

          {/* ALE by Severity Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ALE by Severity */}
            <div>
              <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Annual Loss by Severity
              </h3>
              <div className="space-y-3">
                {aleMetrics.current_exposure?.by_severity && Object.entries(aleMetrics.current_exposure.by_severity).map(([severity, value]) => {
                  const colors = {
                    critical: { bg: 'bg-red-500/20', bar: 'bg-red-500', text: 'text-red-400' },
                    high: { bg: 'bg-orange-500/20', bar: 'bg-orange-500', text: 'text-orange-400' },
                    medium: { bg: 'bg-yellow-500/20', bar: 'bg-yellow-500', text: 'text-yellow-400' },
                    low: { bg: 'bg-green-500/20', bar: 'bg-green-500', text: 'text-green-400' }
                  };
                  const total = aleMetrics.current_exposure.total_ale || 1;
                  const percentage = (value / total) * 100;
                  
                  return (
                    <div 
                      key={severity} 
                      className="space-y-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/vulnerability-hub?severity=${severity}`)}
                      data-testid={`ale-${severity}-drill`}
                    >
                      <div className="flex justify-between text-sm">
                        <span className={`capitalize ${isDarkMode ? colors[severity]?.text || 'text-zinc-400' : 'text-slate-700'}`}>
                          {severity}
                        </span>
                        <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                          {formatCurrency(value)}
                        </span>
                      </div>
                      <div className={`h-2 rounded-full ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                        <div 
                          className={`h-full rounded-full ${colors[severity]?.bar || 'bg-slate-400'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Risks by ALE */}
            <div>
              <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Top 5 Risks by ALE
              </h3>
              <div className="space-y-2">
                {aleMetrics.top_risks?.slice(0, 5).map((risk, idx) => (
                  <div 
                    key={risk.vulnerability_id} 
                    className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-red-500 text-white' : 
                        idx === 1 ? 'bg-orange-500 text-white' : 
                        'bg-zinc-600 text-white'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className={`text-sm font-medium truncate max-w-[200px] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {risk.title}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {risk.cve_id || risk.severity}
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {risk.ale_formatted}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ALE Trend Chart - ROI Demonstration for Executives */}
      {aleMetrics?.ale_trend && aleMetrics.ale_trend.length > 0 && (
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass border border-green-500/20' : 'bg-white border border-green-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                <TrendingDown className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <div>
                <h2 className={`font-inter font-semibold text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Financial Risk Reduction Over Time
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  ROI demonstration - How remediation reduces annual loss expectancy
                </p>
              </div>
            </div>
            <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Projected Annual Savings</p>
              <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.risk_reduction?.savings_formatted || '$0'}
              </p>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
              <AreaChart data={aleMetrics.ale_trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="aleGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                <XAxis 
                  dataKey="month" 
                  stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                  tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                />
                <YAxis 
                  stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                  tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                    border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    color: isDarkMode ? '#ffffff' : '#1f2937'
                  }}
                  formatter={(value, name) => [
                    `$${(value / 1000000).toFixed(2)}M`,
                    name === 'ale' ? 'Risk Exposure' : 'Cumulative Savings'
                  ]}
                />
                <Legend 
                  wrapperStyle={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
                  formatter={(value) => value === 'ale' ? 'Risk Exposure (ALE)' : 'Cumulative Savings'}
                />
                <Area 
                  type="monotone" 
                  dataKey="ale" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  fill="url(#aleGradient)" 
                  name="ale"
                />
                <Area 
                  type="monotone" 
                  dataKey="savings" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  fill="url(#savingsGradient)" 
                  name="savings"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* ROI Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Starting Exposure (6mo ago)</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.ale_trend?.[0] ? `$${(aleMetrics.ale_trend[0].ale / 1000000).toFixed(2)}M` : '-'}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Current Exposure</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {aleMetrics.current_exposure?.total_ale_formatted || '-'}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Total Risk Reduced</p>
              <p className={`text-lg font-bold text-green-500`}>
                {aleMetrics.risk_reduction?.risk_reduction_percentage?.toFixed(1) || 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Trend Over Time - NEW CISO CHART */}
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <h2 className={`font-inter font-semibold text-xl mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          <TrendingDown className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          Risk Trend Over Time
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={riskTrendData}>
              <defs>
                <linearGradient id="riskGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.danger} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={chartColors.danger} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="securityGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.success} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={chartColors.success} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="month" stroke={chartColors.text} fontSize={12} />
              <YAxis stroke={chartColors.text} fontSize={12} domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="risk_score" name="Risk Score" stroke={chartColors.danger} fillOpacity={1} fill="url(#riskGradient)" strokeWidth={2} />
              <Area type="monotone" dataKey="security_score" name="Security Score" stroke={chartColors.success} fillOpacity={1} fill="url(#securityGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* SLA Compliance Metrics - NEW CISO CHART */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <h2 className={`font-inter font-semibold text-xl mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <Timer className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
            SLA Compliance Metrics
          </h2>
          <div className="space-y-4">
            {slaComplianceData.map((item, idx) => (
              <div 
                key={idx} 
                className="space-y-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/vulnerability-hub?severity=${item.name.toLowerCase()}`)}
                data-testid={`sla-${item.name.toLowerCase()}-drill`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-inter ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.name}</span>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target: {item.target}%</span>
                    <span className={`text-sm font-semibold ${item.actual >= item.target ? 'text-green-500' : 'text-red-500'}`}>
                      {item.actual}%
                    </span>
                  </div>
                </div>
                <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${item.actual >= item.target ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${item.actual}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            Click any severity to view vulnerabilities
          </p>
          <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Overall SLA Compliance</span>
              <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {Math.round(slaComplianceData.reduce((acc, item) => acc + (item.actual >= item.target ? 1 : 0), 0) / slaComplianceData.length * 100)}%
              </span>
            </div>
          </div>
        </div>

        {/* Security Posture Score History - NEW CISO CHART */}
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <h2 className={`font-inter font-semibold text-xl mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <BarChart3 className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
            Security Posture History
          </h2>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={securityPostureHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="quarter" stroke={chartColors.text} fontSize={11} />
                <YAxis stroke={chartColors.text} fontSize={11} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="score" name="Security Score" fill={chartColors.primary} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className={`mt-4 grid grid-cols-3 gap-3`}>
            {securityPostureHistory.slice(-1).map((item, idx) => (
              <React.Fragment key={`posture-${idx}`}>
                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.score}%</p>
                  <p className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Current Score</p>
                </div>
                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{item.vulns}</p>
                  <p className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Open Vulns</p>
                </div>
                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`}>{item.assets}</p>
                  <p className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Assets</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Key Initiatives */}
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <h2 className={`font-inter font-semibold text-xl mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Key Security Initiatives</h2>
        <div className="space-y-4">
          {(data?.key_initiatives || [
            { name: 'Zero Trust Implementation', status: 'on_track', progress: 75 },
            { name: 'Endpoint Protection Rollout', status: 'at_risk', progress: 45 },
            { name: 'Security Awareness Training', status: 'on_track', progress: 90 },
            { name: 'SIEM Integration', status: 'on_track', progress: 60 },
          ]).map((initiative, idx) => (
            <div key={idx} className={`rounded-lg p-4 ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-inter ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{initiative.name}</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  initiative.status === 'on_track' 
                    ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                    : isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {initiative.status === 'on_track' ? 'On Track' : 'At Risk'}
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                <div 
                  className={`h-full rounded-full ${
                    initiative.status === 'on_track' ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 'bg-gradient-to-r from-yellow-500 to-orange-400'
                  }`}
                  style={{ width: `${initiative.progress}%` }}
                />
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{initiative.progress}% complete</p>
            </div>
          ))}
        </div>
      </div>

      {/* Crown Jewels Section */}
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`font-inter font-semibold text-xl flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <Crown className="w-5 h-5 text-yellow-500" />
            Crown Jewels
          </h2>
          <button 
            onClick={() => navigate('/crown-jewels')}
            className={`text-xs hover:underline ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
          >
            View All →
          </button>
        </div>
        
        {crownJewels.length === 0 ? (
          <div className="text-center py-8">
            <Crown className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-yellow-400/30' : 'text-yellow-500/30'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No Crown Jewels tagged yet</p>
            <button 
              onClick={() => navigate('/crown-jewels')}
              className={`mt-2 text-xs ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
            >
              Tag your first Crown Jewel →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {crownJewels.slice(0, 6).map((cj) => (
              <div 
                key={cj.id} 
                className={`rounded-lg p-4 border cursor-pointer transition-all ${
                  isDarkMode 
                    ? 'bg-zinc-900/50 border-yellow-500/20 hover:border-yellow-500/40' 
                    : 'bg-yellow-50 border-yellow-200 hover:border-yellow-400'
                }`}
                onClick={() => navigate('/crown-jewels')}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                    <Crown className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className={`font-inter text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{cj.asset_name}</p>
                    <p className={`text-xs capitalize ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{cj.business_unit || 'Unassigned'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                    cj.business_impact === 'critical' ? 'bg-red-500/20 text-red-500' :
                    cj.business_impact === 'high' ? 'bg-orange-500/20 text-orange-500' :
                    'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    {cj.business_impact}
                  </span>
                  <span className={`text-xs ${(cj.vulnerability_count || 0) > 0 ? 'text-orange-500' : 'text-green-500'}`}>
                    {cj.vulnerability_count || 0} vulns
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
